import { QuoteIcon } from "lucide-react"

interface Testimonial {
  /**
   * Testimonial quote
   */
  quote: string;

  /**
   * Founder name
   */
  name: string;

  /**
   * Founder title/company
   */
  title: string;

  /**
   * Founder photo URL
   */
  photo: string;
}

interface TestimonialsSectionProps {
  /**
   * Section title
   */
  title?: string;

  /**
   * Section description
   */
  description?: string;

  /**
   * Array of testimonials
   */
  testimonials?: Testimonial[];

  /**
   * Additional CSS classes
   */
  className?: string;
}

const defaultTestimonials: Testimonial[] = [
  {
    quote: "BitLearn helped me validate my SaaS idea in just 6 weeks. The structured approach and expert feedback were invaluable.",
    name: "Sarah Chen",
    title: "Founder, DataFlow",
    photo: "https://github.com/yusufhilmi.png"
  },
  {
    quote: "The cohort-based learning kept me accountable. I conducted 15 customer interviews and pivoted to a much stronger idea.",
    name: "Michael Rodriguez",
    title: "Founder, TaskHub",
    photo: "https://github.com/kdrnp.png"
  },
  {
    quote: "Best investment I made for my startup. The coaching sessions alone were worth 10x the price.",
    name: "Emily Thompson",
    title: "Founder, HealthTrack",
    photo: "https://github.com/yahyabedirhan.png"
  }
]

export function TestimonialsSection({
  title = "Trusted by Founders",
  description = "See what founders are saying about their experience with BitLearn",
  testimonials = defaultTestimonials,
  className = ""
}: TestimonialsSectionProps) {
  return (
    <section className={`bg-background py-16 md:py-24 ${className}`}>
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground">
            {description}
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={index}
              className="bg-card border border-border rounded-lg p-8 hover:shadow-lg transition-shadow"
            >
              {/* Quote Icon */}
              <div className="mb-6">
                <QuoteIcon className="w-10 h-10 text-primary/40" />
              </div>

              {/* Quote */}
              <p className="text-foreground leading-relaxed mb-6">
                "{testimonial.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <img
                  src={testimonial.photo}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full"
                />
                <div>
                  <div className="font-semibold text-foreground">
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {testimonial.title}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
